import * as React from 'react';
import { ChangeEntityLog, ChangeLog, EntityType } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { ChangeKind } from '../../models';
import { Table } from 'reactstrap';
import ToolTip from '../ToolTip';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { openEntity } from '../../store/entities/actions';
import { Entity, EntityType as StateEntityType } from '../../store/entities/state';
import { getAppointmentAsync, getClientAsync, getClientOrderByIdAsync, getPackAsync, getProfessionalAsync } from '../../ajax/requests';
import AppButton from '../AppButton';

export interface ChangeLogInfoProps {
    changeLog: (ChangeLog|ChangeEntityLog)[]
    showDate?: boolean
    showTime?: boolean
}

const entityTypeTK = (entityType: EntityType) => {
    switch(entityType) {
        case EntityType.Appointment: return TK.appointment
        case EntityType.Client: return TK.client
        case EntityType.FileReference: return TK.file
        case EntityType.Pack: return TK.pack
        case EntityType.Order: return TK.orders
        case EntityType.Professional: return TK.professional
        case EntityType.Report: return TK.report
        case EntityType.Settings: return TK.settings
        case EntityType.User: return TK.user
        case EntityType.Campaign: return TK.campaign
        case EntityType.Expense: return TK.expense
        case EntityType.Stock: return TK.stock
        case EntityType.SettingsClientsLogin: return TK.clientsLogin
        case EntityType.SettingsGeneral: return TK.settingsGeneral
        case EntityType.SettingsPrices: return TK.settingsPrices
        case EntityType.SettingsExpenses: return TK.settingsExpenses
        case EntityType.SettingsExtras: return TK.settingsExtras
        case EntityType.SettingsEmail: return TK.settingsEmail
        case EntityType.SettingsReports: return TK.settingsReports
        case EntityType.SettingsPacks: return TK.settingsPacks
        case EntityType.SettingsReminders: return TK.settingsReminders
        case EntityType.SettingsSet: return TK.settingsSet
        case EntityType.SettingsPayments: return TK.paymentProfiles
        default: return TK.unknown
    }
}

const hasEntity = (change: ChangeLog | ChangeEntityLog): change is ChangeEntityLog => {
    return (change as ChangeEntityLog)?.entityId !== undefined;
}

const openableEntities = new Map<EntityType, [StateEntityType, (id: string) => Promise<Entity>]>([
    [EntityType.Appointment, ['appointments', getAppointmentAsync]],
    [EntityType.Client, ['clients', getClientAsync]],
    [EntityType.Pack, ['clientPacks', getPackAsync]],
    [EntityType.Professional, ['professionals', getProfessionalAsync]],
    [EntityType.Order, ['orders', getClientOrderByIdAsync]],
])

const ChangeLogTable: React.FC<ChangeLogInfoProps> = ({
    changeLog,
    showDate = true,
    showTime = false,
}) => {

    const t = useTranslations();
    const dispatch = useDispatch();

    const getActionCell = useCallback((idx: number, log: ChangeLog) => {
        switch(log.changeKind) {
            case ChangeKind.Deleted: return t(TK.deleted);
            case ChangeKind.Created: return t(TK.created);
            case ChangeKind.Updated: return (
                <ToolTip key={idx} target={`action${idx}`} toolTipContent={getToolTipContent(log)}>
                    <span id={`action${idx}`}>{t(TK.changed)}</span>
                </ToolTip>);
            default: return t(TK.unknown);
        }
    }, [])

    const getToolTipContent = (log: ChangeLog) => (
        <div>
            { log.details.map(detail => (<p>{detail}</p>)) }
        </div>
    );

    const showEntityId = useMemo(() => hasEntity(changeLog[0]), [changeLog])

    const onClick = useCallback((log: ChangeEntityLog) => () => {
        const [entityTypeToOpen, getRequest] = openableEntities.get(log.entityType) ?? []
        if (entityTypeToOpen && getRequest) {
            dispatch(openEntity(entityTypeToOpen, log.entityId, () => getRequest(log.entityId)))
        }
    }, [dispatch])

    return (
        <Table size="sm">
            <thead>
                <tr>
                    <th>{t(showDate ? TK.date : TK.time)}</th>
                    {showEntityId && <th>{t(TK.entity)}</th>}
                    <th>{t(TK.action)}</th>
                    <th>{t(TK.username)}</th>
                </tr>
            </thead>
            <tbody>
                {changeLog.map((log, i) =>
                    <tr key={i}>
                        <td>{showDate && log.timestamp.substring(0, 10)} {showTime && log.timestamp.substring(11, 16)}</td>
                        {hasEntity(log) && <td>
                            <AppButton
                                size="sm"
                                color="link"
                                disabled={log.changeKind === ChangeKind.Deleted || !openableEntities.has(log.entityType)}
                                onClick={onClick(log)}
                            >
                                {t(entityTypeTK(log.entityType))} ({log.entityDescription ?? ''})
                            </AppButton>
                        </td>}
                        <td id={`action${i}`}>{getActionCell(i, log)}</td>
                        <td>{log.username}</td>
                    </tr>
                )}
            </tbody>
        </Table>
    );
}

export default ChangeLogTable;